import { useCallback, useState } from 'react';

import toast from 'components/partials/toast/toast';

import { useUpdateAidModelArchiveState } from './use-aid-models';

import { handleAidModelError } from '../aid-studio-details/utils/aid-model-error-handler';

import {
  ARCHIVE_TOAST_MESSAGE,
  ARCHIVE_TOAST_TITLE,
  RESTORE_TOAST_MESSAGE,
  RESTORE_TOAST_TITLE,
} from '../aid-studio.constants';

interface UseModelArchiveParams {
  userId: string;
  clientId: number;
  onArchived?: () => void;
  onRestored?: () => void;
}

interface UseModelArchiveResult {
  isArchiveModalOpen: boolean;
  isUpdating: boolean;
  requestArchive: (modelId: string | number) => void;
  cancelArchive: () => void;
  confirmArchive: () => Promise<void>;
  restore: (modelId: string | number) => Promise<void>;
}

export const useModelArchive = ({
  userId,
  clientId,
  onArchived,
  onRestored,
}: UseModelArchiveParams): UseModelArchiveResult => {
  const [pendingModelId, setPendingModelId] = useState<string | number | null>(
    null
  );

  const updateArchiveState = useUpdateAidModelArchiveState({
    userId,
    clientId,
  });

  const requestArchive = useCallback(
    (modelId: string | number) => setPendingModelId(modelId),
    []
  );

  const cancelArchive = useCallback(() => setPendingModelId(null), []);

  const confirmArchive = useCallback(async (): Promise<void> => {
    if (pendingModelId == null) return;

    try {
      await updateArchiveState.mutateAsync({
        modelId: pendingModelId,
        data: { archived: true },
      });
      toast.success({
        title: ARCHIVE_TOAST_TITLE,
        message: ARCHIVE_TOAST_MESSAGE,
      });
      setPendingModelId(null);
      onArchived?.();
    } catch (error) {
      setPendingModelId(null);
      handleAidModelError(error, 'Failed to archive model');
    }
  }, [pendingModelId, updateArchiveState, onArchived]);

  const restore = useCallback(
    async (modelId: string | number): Promise<void> => {
      try {
        await updateArchiveState.mutateAsync({
          modelId,
          data: { archived: false },
        });
        toast.success({
          title: RESTORE_TOAST_TITLE,
          message: RESTORE_TOAST_MESSAGE,
        });
        onRestored?.();
      } catch (error) {
        handleAidModelError(error, 'Failed to restore model');
      }
    },
    [updateArchiveState, onRestored]
  );

  return {
    isArchiveModalOpen: pendingModelId != null,
    isUpdating: updateArchiveState.isPending,
    requestArchive,
    cancelArchive,
    confirmArchive,
    restore,
  };
};
